// src/pages/dms/DBMS.jsx
import React, { useState } from 'react';
import {
  Typography, Box, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Paper, CircularProgress, Alert, TextField,
  MenuItem, Select, InputLabel, FormControl, useMediaQuery,
  Breadcrumbs, Link as MuiLink // Add Breadcrumbs and MuiLink
} from '@mui/material';

import { useTheme } from "@mui/material/styles";
import { ref, update, get } from "firebase/database";
import { database } from "../../firebase";
import useAuthCheck from "../../hooks/useAuthCheck"; // Import useAuthCheck
import { useOutletContext, useNavigate } from "react-router-dom"; // Import useOutletContext and useNavigate

// --- UTILITY FUNCTIONS ---
const generateUniqueId = () => Date.now().toString() + Math.random().toString(36).substring(2, 9);

const transformToFirebaseJson = (nodes) => {
  if (!nodes || nodes.length === 0) return {};

  const firebaseJson = {};
  nodes.forEach(node => {
    // Handle cases where node.name might be empty for root/sub/child nodes
    const nodeKey = node.name && node.name.trim() !== '' ? node.name.trim() : generateUniqueId();

    if (node.type === 'property') {
      firebaseJson[nodeKey] = node.value;
    } else {
      // Node, Subnode, Child Node akan menjadi objek.
      // Rekursif untuk children
      const childrenJson = transformToFirebaseJson(node.children);
      firebaseJson[nodeKey] = childrenJson;
    }
  });
  return firebaseJson;
};


const formatTreeAsIndentedText = (nodes, indentLevel = 0) => {
  if (!nodes || nodes.length === 0) return '';

  const indent = '  '.repeat(indentLevel);
  let text = '';

  nodes.forEach(node => {
    let line = '';
    const nodeName = node.name && node.name.trim() !== '' ? `"${node.name.trim()}"` : `<${node.type} Tanpa Nama>`;

    if (node.type === 'property') {
      const displayValue = typeof node.value === 'string' ? `"${node.value}"` : String(node.value);
      line = `${indent}${nodeName}: ${displayValue}`;
    } else {
      line = `${indent}${nodeName}`;
      if (node.children && node.children.length > 0) {
        line += ' {';
      } else {
        line += ' {}'; // Jika node/sub-node/child-node kosong
      }
    }
    text += line + '\n';

    if (node.children && node.children.length > 0 && node.type !== 'property') {
      text += formatTreeAsIndentedText(node.children, indentLevel + 1);
      // Close brace for the current node's children block
      // Only add '}' if a '{' was implicitly opened by children existing and not being a property
      const currentLineHasOpenBrace = line.endsWith(' {');
      if (currentLineHasOpenBrace && node.type !== 'property') {
         text += `${indent}}\n`;
      }
    }
  });

  return text;
};

// --- NODE EDITOR COMPONENT ---
const NodeEditor = ({ node, onUpdate, onDelete, level = 'root-node' }) => {
  const [name, setName] = useState(node.name);
  const [propertyValue, setPropertyValue] = useState(node.value !== null ? node.value : '');
  const [propertyDataType, setPropertyDataType] = useState(node.dataType || 'string');

  React.useEffect(() => {
    setName(node.name);
    // Ensure propertyValue is set correctly based on its actual type in node.value
    // This handles cases where node.value might be boolean/number from previous save/load
    if (node.type === 'property') {
        if (node.dataType === 'boolean') {
            setPropertyValue(String(node.value)); // 'true' or 'false'
        } else if (node.dataType === 'number') {
            setPropertyValue(String(node.value)); // Convert number to string for TextField
        } else {
            setPropertyValue(node.value !== null ? node.value : '');
        }
        setPropertyDataType(node.dataType || 'string');
    } else {
        setPropertyValue(''); // Not a property, so value is irrelevant
        setPropertyDataType('string'); // Not a property, so dataType is irrelevant
    }
  }, [node]);


  const handleNameChange = (e) => {
    setName(e.target.value);
    onUpdate({ ...node, name: e.target.value });
  };

  const handlePropertyValueChange = (e) => {
    let value = e.target.value;
    let typedValue = value;

    if (propertyDataType === 'number') {
      typedValue = Number(value);
      if (isNaN(typedValue)) typedValue = 0;
    } else if (propertyDataType === 'boolean') {
      typedValue = (value === 'true');
    }
    setPropertyValue(value);
    onUpdate({ ...node, value: typedValue });
  };

  const handlePropertyDataTypeChange = (e) => {
    const newDataType = e.target.value;
    setPropertyDataType(newDataType);
    let initialValueForNewType;

    // Convert existing value to new type or set default
    if (newDataType === 'number') {
      initialValueForNewType = Number(propertyValue) || 0;
    } else if (newDataType === 'boolean') {
      initialValueForNewType = (String(propertyValue).toLowerCase() === 'true');
    } else { // string
      initialValueForNewType = String(propertyValue);
    }
    setPropertyValue(String(initialValueForNewType)); // Keep local state as string for TextField/Select
    onUpdate({ ...node, value: initialValueForNewType, dataType: newDataType });
  };

  const addChild = (type) => {
    const newChild = {
      id: generateUniqueId(),
      name: '',
      type: type,
      value: null,
      children: type === 'property' ? null : [],
      dataType: type === 'property' ? 'string' : undefined,
    };
    onUpdate({ ...node, children: [...(node.children || []), newChild] });
  };

  return (
    <Box sx={{ border: '1px solid #ccc', p: 2, mb: 1, ml: level !== 'root-node' ? 4 : 0, borderRadius: 1, bgcolor: level === 'property' ? '#f5f5f5' : '#fff' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <TextField
          label={
            level === 'root-node' ? 'Nama Node Utama' :
            level === 'sub-node' ? 'Nama Subnode' :
            level === 'child-node' ? 'Nama Child Node' : 'Nama Properti'
          }
          value={name}
          onChange={handleNameChange}
          size="small"
          sx={{ mr: 1, mb: 1, flexGrow: 1 }}
        />

        {level === 'property' && (
          <>
            <FormControl size="small" sx={{ mr: 1, mb: 1, minWidth: 120 }}>
              <InputLabel id="data-type-label">Tipe Data</InputLabel>
              <Select
                labelId="data-type-label"
                value={propertyDataType}
                label="Tipe Data"
                onChange={handlePropertyDataTypeChange}
              >
                <MenuItem value="string">String</MenuItem>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="boolean">Boolean</MenuItem>
              </Select>
            </FormControl>

            {propertyDataType === 'boolean' ? (
              <FormControl size="small" sx={{ mr: 1, mb: 1, flexGrow: 1 }}>
                <InputLabel id="boolean-value-label">Nilai Boolean</InputLabel>
                <Select
                  labelId="boolean-value-label"
                  value={String(propertyValue)}
                  label="Nilai Boolean"
                  onChange={handlePropertyValueChange}
                >
                  <MenuItem value="true">True</MenuItem>
                  <MenuItem value="false">False</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Nilai Properti"
                value={propertyValue}
                onChange={handlePropertyValueChange}
                type={propertyDataType === 'number' ? 'number' : 'text'}
                size="small"
                sx={{ mr: 1, mb: 1, flexGrow: 1 }}
              />
            )}
          </>
        )}

        <Button onClick={() => onDelete(node.id)} color="error" size="small" sx={{ mb: 1 }}>
          Hapus
        </Button>

        {level !== 'property' && (
          <Box sx={{ ml: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(level === 'root-node' || level === 'sub-node') && (
              <Button onClick={() => addChild('sub-node')} size="small" variant="outlined">
                Tambah Subnode
              </Button>
            )}
            {(level === 'root-node' || level === 'sub-node' || level === 'child-node') && (
              <Button onClick={() => addChild('child-node')} size="small" variant="outlined">
                Tambah Child Node
              </Button>
            )}
            {level === 'child-node' && (
              <Button onClick={() => addChild('property')} size="small" variant="outlined">
                Tambah Properti
              </Button>
            )}
          </Box>
        )}
      </Box>

      {node.children && node.children.map(child => (
        <NodeEditor
          key={child.id}
          node={child}
          onUpdate={(updatedChild) => {
            onUpdate({
              ...node,
              children: node.children.map(c => c.id === updatedChild.id ? updatedChild : c)
            });
          }}
          onDelete={(childIdToDelete) => {
            onUpdate({
              ...node,
              children: node.children.filter(c => c.id !== childIdToDelete)
            });
          }}
          level={child.type}
        />
      ))}
    </Box>
  );
};


// --- DBMS MAIN COMPONENT ---
function DBMS() {
  const { user, loading } = useAuthCheck(); // Use useAuthCheck hook
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate(); // For navigating with breadcrumbs
  const { breadcrumbs } = useOutletContext(); // Get breadcrumbs from Layout context

  const [rootNodes, setRootNodes] = useState([
    {
      id: generateUniqueId(),
      name: '',
      type: 'root-node',
      value: null,
      children: []
    }
  ]);
  const [saveLoading, setSaveLoading] = useState(false); // Renamed to avoid conflict with auth loading
  const [message, setMessage] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);

  // Handle authentication loading and user check
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Anda harus login untuk mengakses halaman ini
        </Alert>
      </Box>
    );
  }

  // Updates a specific node within the complex rootNodes state structure
  const updateNodeInTree = (updatedNode) => {
    setRootNodes(prevNodes => prevNodes.map(node =>
      node.id === updatedNode.id ? updatedNode : node
    ));
  };

  // Deletes a specific root node from the rootNodes array
  const deleteRootNode = (idToDelete) => {
    setRootNodes(prevNodes => prevNodes.filter(node => node.id !== idToDelete));
  };

  const handleAddRootNode = () => {
    setRootNodes(prevNodes => [
      ...prevNodes,
      {
        id: generateUniqueId(),
        name: '',
        type: 'root-node',
        value: null,
        children: []
      }
    ]);
  };

  const handlePreview = () => {
    setOpenPreview(true);
  };

const handleSaveToFirebase = async () => {
  setSaveLoading(true); // Use saveLoading
  setMessage(null);

  if (rootNodes.length === 0) {
    setMessage({ type: 'warning', text: 'Tidak ada struktur untuk disimpan.' });
    setSaveLoading(false);
    return;
  }

  try {
    // 1. Ambil data yang sudah ada di Firebase
    const dbRef = ref(database, '/');
    const snapshot = await get(dbRef);
    const existingData = snapshot.val() || {}; // Jika tidak ada data, gunakan objek kosong

    // 2. Gabungkan data yang sudah ada dengan data baru dari UI
    const combinedDataToSave = { ...existingData }; // Salin data yang sudah ada

    for (const rootNode of rootNodes) {
      if (!rootNode.name || rootNode.name.trim() === '') {
        setMessage({ type: 'error', text: `Node Utama dengan ID '${rootNode.id}' tidak memiliki nama. Harap isi nama untuk semua Node Utama.` });
        setSaveLoading(false);
        return;
      }

      const nodeData = transformToFirebaseJson(rootNode.children);
      const rootNodeName = rootNode.name.trim();

      // Jika rootNode sudah ada di Firebase, gabungkan children-nya
      if (existingData[rootNodeName]) {
        combinedDataToSave[rootNodeName] = {
          ...existingData[rootNodeName], // Pertahankan data yang sudah ada
          ...nodeData,                  // Tambahkan data baru
        };
      } else {
        combinedDataToSave[rootNodeName] = nodeData;
      }
    }

    // 3. Simpan data gabungan ke Firebase
    await update(dbRef, combinedDataToSave);
    setMessage({ type: 'success', text: `Semua struktur database berhasil diperbarui di Firebase!` });

  } catch (error) {
    console.error("Error saving to Firebase:", error);
    setMessage({ type: 'error', text: `Gagal menyimpan ke Firebase: ${error.message}` });
  } finally {
    setSaveLoading(false); // Use saveLoading
  }
};

  return (
    <Box sx={{ pb: isMobile ? 8 : 0 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            {breadcrumbs && breadcrumbs.map((crumb, index) => (
                // Render Link if path exists, otherwise render Typography
                crumb.path ? (
                    <MuiLink
                        key={index}
                        underline="hover"
                        color="inherit"
                        onClick={() => navigate(crumb.path)}
                        sx={{ cursor: 'pointer' }}
                    >
                        {crumb.label}
                    </MuiLink>
                ) : (
                    <Typography key={index} color="text.primary">
                        {crumb.label}
                    </Typography>
                )
            ))}
        </Breadcrumbs>

      <Paper elevation={3} sx={{ p: 3, mt: 0, mb: 0 }}>
        <Typography variant="h4" gutterBottom>
          Database Management System (DBMS)
        </Typography>
        <Typography variant="body1" paragraph>
          Rancang struktur node Firebase Anda secara dinamis, lihat pratinjau, dan simpan.
        </Typography>

        {rootNodes.map((node, index) => (
          <Box key={node.id} sx={{ mb: 2, border: '1px dashed grey', p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
                Node Utama #{index + 1}
                {rootNodes.length > 1 && ( // Allow deleting if more than one root node
                    <Button
                        onClick={() => deleteRootNode(node.id)}
                        color="error"
                        size="small"
                        sx={{ ml: 2 }}
                    >
                        Hapus Node Utama Ini
                    </Button>
                )}
            </Typography>
            <NodeEditor
              node={node}
              onUpdate={updateNodeInTree}
              onDelete={(childId) => { // This onDelete is for children of root, not the root itself
                updateNodeInTree({
                    ...node,
                    children: node.children.filter(c => c.id !== childId)
                });
              }}
              level="root-node"
            />
          </Box>
        ))}

        <Button
            variant="outlined"
            color="primary"
            onClick={handleAddRootNode}
            sx={{ mt: 2 }}
        >
            Tambah Node Utama Baru
        </Button>

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handlePreview}
            disabled={rootNodes.length === 0}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveToFirebase}
            disabled={saveLoading || rootNodes.length === 0 || rootNodes.some(node => !node.name || node.name.trim() === '')}
          >
            {saveLoading ? <CircularProgress size={24} /> : 'Simpan ke Firebase'}
          </Button>
        </Box>

        {message && (
          <Alert severity={message.type} sx={{ mt: 2 }}>
            {message.text}
          </Alert>
        )}
      </Paper>

      <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="md" fullWidth>
        <DialogTitle>Pratinjau Struktur Database</DialogTitle>
        <DialogContent>
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#f0f0f0', overflowX: 'auto' }}>
            <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.85rem' }}>
              {(() => {
                let previewText = '{\n';
                if (rootNodes.length === 0) {
                    previewText += '  // Tidak ada struktur untuk pratinjau\n';
                } else {
                    rootNodes.forEach(rootNode => {
                        const rootNodeName = rootNode.name && rootNode.name.trim() !== '' ? `"${rootNode.name.trim()}"` : `<Node Utama Tanpa Nama>`;
                        previewText += `  ${rootNodeName}: {\n`;
                        previewText += formatTreeAsIndentedText(rootNode.children, 2);
                        previewText += `  }\n`;
                    });
                }
                previewText += '}';
                return previewText;
              })()}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ pt: "0px", pb: "20px", pl: "24px", pr: "24px" }}>
          <Button onClick={() => setOpenPreview(false)}>Tutup</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DBMS;